/**
 * Per-platform "rate us" + feedback configuration for Nonogram.
 *
 * This file is the single source of truth for what the Rate Us button does on
 * each platform the game ships to. The `@org/rate-us` lib handles detecting
 * the current platform, picking the right entry from this config, and
 * dispatching the action.
 *
 * To change where the button sends players on a given platform, edit the
 * matching entry below. New platforms can be added as `overrides` keys — any
 * platform without an override falls through to `defaults`.
 */
import type { RateUsConfig } from '@org/rate-us';

/**
 * 12-character Microsoft Store Product ID for Nonogram on Windows. This ID is
 * what both the `ms-windows-store://review` protocol handler and the
 * `apps.microsoft.com/detail/...` web URL are built from.
 */
const MICROSOFT_STORE_ID = '9PKJS4Z0HT6Q';

/**
 * Web detail page used as the fallback "rate" target on any browser-based
 * platform (macOS / Linux / desktop web / etc.).
 */
const MICROSOFT_STORE_WEB_URL = `https://apps.microsoft.com/detail/${MICROSOFT_STORE_ID}`;

/**
 * Private feedback form used for the "Sad" sentiment path.
 */
const FEEDBACK_FORM_URL = 'https://sliceofgames.com/nonogram/feedback';

/**
 * Contact email used by the mailto fallback on platforms where a web form is
 * not a good fit (e.g. TV / embedded shells).
 */
const FEEDBACK_EMAIL = 'hello@sliceofgames.com';

export const rateUsConfig: RateUsConfig = {
  defaults: {
    // Anything without a platform-specific override opens the web store page
    // in a new tab and routes feedback to the web form.
    rate: { type: 'url', url: MICROSOFT_STORE_WEB_URL, target: '_blank' },
    feedback: { type: 'url', url: FEEDBACK_FORM_URL, target: '_blank' },
  },
  overrides: {
    windows: {
      // Native Microsoft Store review flow with web fallback.
      rate: { type: 'microsoft-store', storeId: MICROSOFT_STORE_ID },
    },
    ios: {
      // TODO: replace with the real App Store ID once the iOS build ships.
      rate: { type: 'app-store', appId: '0000000000' },
      feedback: {
        type: 'mailto',
        to: FEEDBACK_EMAIL,
        subject: 'Nonogram feedback (iOS)',
      },
    },
    android: {
      // TODO: replace with the real Play Store package once the Android build
      // ships.
      rate: { type: 'play-store', packageName: 'com.sliceofgames.nonogram' },
      feedback: {
        type: 'mailto',
        to: FEEDBACK_EMAIL,
        subject: 'Nonogram feedback (Android)',
      },
    },
  },
};
